import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  brandingFooterLine,
  brandingPoweredByLine,
  loadBrandingLogoDataUrl,
  pdfImageFormat,
  resolveExportBranding,
} from './tenantBranding';

function safeFilename(name) {
  return String(name || 'document')
    .replace(/[^\w\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
}

const BRAND = {
  primary: [15, 23, 42],
  accent: [30, 64, 175],
  muted: [100, 116, 139],
  border: [203, 213, 225],
};

function tsh(n) {
  return `TZS ${Math.round(Number(n) || 0).toLocaleString('en-TZ')}`;
}

function drawPageBorder(doc, pageWidth, pageHeight) {
  const inset = 14;
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.85);
  doc.roundedRect(inset, inset, pageWidth - 2 * inset, pageHeight - 2 * inset, 7, 7, 'S');
}

function addFooter(doc, pageWidth, pageHeight, margin, branding, poweredBy, page, total) {
  const bottomY = pageHeight - margin;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(`Page ${page} of ${total}`, pageWidth / 2, bottomY - 18, { align: 'center' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...BRAND.primary);
  doc.text(brandingFooterLine(branding), pageWidth / 2, bottomY - 32, { align: 'center' });
  if (poweredBy) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.text(poweredBy, pageWidth / 2, bottomY - 44, { align: 'center' });
  }
}

export async function exportLoanPackPdf(snapshot, user, labels = {}) {
  if (!snapshot) return;

  const branding = resolveExportBranding({ branding: labels.branding });
  const poweredBy = brandingPoweredByLine(branding);
  const generatedAt = new Date().toLocaleString('en-GB', { hour12: false });
  const L = (key, fallback) => labels[key] || fallback;

  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 42;
  const logoDataUrl = await loadBrandingLogoDataUrl(branding);

  drawPageBorder(doc, pageWidth, pageHeight);

  let y = margin + 8;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, pdfImageFormat(logoDataUrl), pageWidth / 2 - 22, y, 44, 44);
    y += 52;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.accent);
  doc.text(String(branding.businessName).toUpperCase(), pageWidth / 2, y, { align: 'center' });
  y += 22;
  doc.setFontSize(18);
  doc.setTextColor(...BRAND.primary);
  doc.text(L('title', 'Loan Application Pack'), pageWidth / 2, y, { align: 'center' });
  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.muted);
  doc.text(`${L('period', 'Period')}: ${snapshot.periodLabel}`, pageWidth / 2, y, { align: 'center' });
  y += 14;
  doc.text(`${L('generated', 'Generated')}: ${generatedAt}`, pageWidth / 2, y, { align: 'center' });
  if (branding.phone) {
    y += 12;
    doc.text(`${L('phone', 'Phone')}: ${branding.phone}`, pageWidth / 2, y, { align: 'center' });
  }
  if (branding.address) {
    y += 12;
    const wrapped = doc.splitTextToSize(String(branding.address), pageWidth - margin * 2);
    doc.text(wrapped, pageWidth / 2, y, { align: 'center' });
    y += wrapped.length * 11;
  }

  y += 20;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.primary);
  doc.text(L('executiveSummary', 'Executive summary'), margin, y);
  y += 8;

  const { totals, stock, debts, flags } = snapshot;
  const summaryBody = [
    [L('totalRevenue', 'Total recorded revenue'), tsh(totals.totalRevenue)],
    [L('posRevenue', 'POS sales revenue'), tsh(totals.posRevenue)],
    [L('posProfit', 'POS gross profit'), tsh(totals.posProfit)],
    [L('totalExpenses', 'Total expenses'), tsh(totals.expenses)],
    [L('netEstimate', 'Estimated net (profit − expenses)'), tsh(totals.netEstimate)],
    [L('stockValue', 'Inventory value (at cost)'), tsh(stock.stockValue)],
    [L('stockProducts', 'Products in stock'), String(stock.productCount)],
  ];
  if (flags.showDebts) {
    summaryBody.push(
      [L('receivables', 'Customer receivables (open)'), tsh(debts.totalOwedTzs)],
      [L('openDebts', 'Open debt accounts'), String(debts.openCount)]
    );
  }
  if (flags.factory) {
    summaryBody.push(
      [L('bricksNet', 'Bricks produced (net)'), `${Math.round(totals.bricksNet).toLocaleString()} pcs`],
      [L('deliveryRevenue', 'Delivery revenue'), tsh(totals.deliveryRevenue)]
    );
  }
  if (flags.showPrinting) {
    summaryBody.push([L('printCollected', 'Printing income collected'), tsh(totals.printCollected)]);
  }

  autoTable(doc, {
    startY: y + 4,
    body: summaryBody,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 5 },
    columnStyles: {
      0: { cellWidth: 220, fontStyle: 'bold', fillColor: [248, 250, 252] },
      1: { halign: 'right' },
    },
    margin: { left: margin, right: margin },
  });

  y = (doc.lastAutoTable?.finalY || y) + 22;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(L('monthlyBreakdown', 'Monthly breakdown'), margin, y);

  const monthHead = [
    L('month', 'Month'),
    L('posRevenue', 'POS revenue'),
    L('posProfit', 'POS profit'),
    L('expenses', 'Expenses'),
    L('netEstimate', 'Net est.'),
  ];
  const monthBody = snapshot.rows.map((r) => [
    r.month,
    tsh(r.posRevenue),
    tsh(r.posProfit),
    tsh(r.expenses),
    tsh(r.netEstimate),
  ]);
  monthBody.push([
    L('total', 'TOTAL'),
    tsh(totals.posRevenue),
    tsh(totals.posProfit),
    tsh(totals.expenses),
    tsh(totals.netEstimate),
  ]);

  autoTable(doc, {
    startY: y + 6,
    head: [monthHead],
    body: monthBody,
    theme: 'grid',
    headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255] },
    styles: { fontSize: 8.5, cellPadding: 4 },
    margin: { left: margin, right: margin, bottom: 70 },
    didParseCell: (ctx) => {
      if (ctx.section === 'body' && ctx.row.index === monthBody.length - 1) {
        ctx.cell.styles.fontStyle = 'bold';
        ctx.cell.styles.fillColor = [241, 245, 249];
      }
    },
  });

  if (flags.factory) {
    doc.addPage();
    drawPageBorder(doc, pageWidth, pageHeight);
    let fy = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...BRAND.primary);
    doc.text(L('factorySection', 'Factory production & deliveries'), margin, fy);
    autoTable(doc, {
      startY: fy + 10,
      head: [[L('month', 'Month'), L('bricksNet', 'Bricks net'), L('deliveryPieces', 'Pieces delivered'), L('deliveryRevenue', 'Delivery revenue')]],
      body: [
        ...snapshot.rows.map((r) => [
          r.month,
          `${Math.round(r.bricksNet).toLocaleString()}`,
          `${Math.round(r.deliveryPieces).toLocaleString()}`,
          tsh(r.deliveryRevenue),
        ]),
        [
          L('total', 'TOTAL'),
          `${Math.round(totals.bricksNet).toLocaleString()}`,
          `${Math.round(totals.deliveryPieces).toLocaleString()}`,
          tsh(totals.deliveryRevenue),
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: [5, 120, 84], textColor: [255, 255, 255] },
      styles: { fontSize: 8.5 },
      margin: { left: margin, right: margin, bottom: 70 },
    });
  }

  const expensePageNeeded = totals.expenses > 0;
  if (expensePageNeeded) {
    doc.addPage();
    drawPageBorder(doc, pageWidth, pageHeight);
    let ey = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(L('expenseSplit', 'Expense split (period total)'), margin, ey);
    autoTable(doc, {
      startY: ey + 10,
      head: [[L('category', 'Category'), L('amount', 'Amount')]],
      body: [
        [L('expenseBoss', 'Owner / business'), tsh(totals.expenseBoss)],
        [L('expenseStaff', 'Staff & payroll'), tsh(totals.expenseStaff)],
        [L('expenseOffice', 'Office & operations'), tsh(totals.expenseOffice)],
        [L('total', 'TOTAL'), tsh(totals.expenses)],
      ],
      theme: 'grid',
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255] },
      margin: { left: margin, right: margin, bottom: 90 },
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    drawPageBorder(doc, pageWidth, pageHeight);
    addFooter(doc, pageWidth, pageHeight, margin, branding, poweredBy, p, totalPages);
    if (p === totalPages) {
      const noteY = pageHeight - margin - 58;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      doc.setTextColor(...BRAND.muted);
      const disclaimer = L(
        'disclaimer',
        'This pack is generated from business records in cbonline app. It supports loan applications but does not replace audited financial statements, tax filings, or bank statements.'
      );
      doc.text(doc.splitTextToSize(disclaimer, pageWidth - margin * 2), margin, noteY);
    }
  }

  const filename = `${safeFilename(branding.businessName)}_loan_pack_${snapshot.periodLabel.replace(/\s+/g, '_')}.pdf`;
  doc.save(filename);
}
